import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import TableCheckBox from 'components/table/TableCheckBox'
import classNames from 'classnames'
import NoneAnchor from 'components/NoneAnchor'
import { DashboardInfoTypes, DashboardTypes } from 'features/Codes'
import { BaseCell } from 'features/dashboard/table/column/DashboardCells'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import NullableNumber from 'features/dashboard/table/shared/NullableNumber'
import { useDashboardTableSelector } from 'features/dashboard/utils/hooks/useDashboardTableSelector'
import Tooltip from 'components/tooltip/Tooltip'
import { useDashboardTableActions } from 'features/dashboard/utils/hooks/useDashboardTableActions'
import { useDashboardTypeName } from 'features/dashboard/utils/hooks/useDashboardTypeName'
import SortArrow from 'features/dashboard/table/shared/SortArrow'
import Loading from 'components/loading/Loading'
import { oppositeSortOrder } from 'utils'
import {
  getDomainKey,
  RateTypes,
} from 'features/dashboard/utils/dashboardUtils'
import { setInfo } from 'features/dashboard/info/dashboardInfoSlice'
import {
  resetActives,
  setAdgroupId,
} from 'features/dashboard/tree/dashboardTreeSlice'

import { ColumnSorts, MetricColumns } from '../../../Columns'

export const EmptyHeader = () => <BaseCell />

export const SelectionHeader = ({ data }) => {
  const dispatch = useDispatch()
  const { type } = useSelector(state => state.dashboard.common)
  const { selectedIds, editable, isLoading } = useDashboardTableSelector()
  const { setSelectedIds } = useDashboardTableActions()
  const key = getDomainKey(type)
  const selectableData = data.filter(({ deleted }) => !deleted)
  const allChecked = useMemo(
    () =>
      selectedIds.length > 0 &&
      selectableData.every(item => selectedIds.includes(item[key])),
    [selectableData, selectedIds, key]
  )
  const disabled = editable || isLoading
  const handleCheckedChange = useCallback(
    checked => {
      const ids = checked ? selectableData.map(item => item[key]) : []
      dispatch(setSelectedIds(ids))
    },
    [dispatch, selectableData, key, setSelectedIds]
  )
  return (
    <BaseCell center className="th_select">
      <TableCheckBox
        checked={allChecked}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
      />
    </BaseCell>
  )
}
SelectionHeader.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      deleted: PropTypes.bool,
    })
  ),
}

export const BaseHeader = ({ column: { title, tooltip } }) => (
  <BaseCell>
    {title}
    {tooltip && <HelpTooltip container="dashboardWrap">{tooltip}</HelpTooltip>}
  </BaseCell>
)
BaseHeader.propTypes = {
  column: PropTypes.shape({
    title: PropTypes.string,
    tooltip: PropTypes.node,
  }),
}

export const SortableHeader = ({
  column: { originalId, title, tooltip },
  disabled = false,
}) => {
  const dispatch = useDispatch()
  const { sort, isLoading, editable } = useDashboardTableSelector()
  const { setSort, setPage } = useDashboardTableActions()
  const sortItem = ColumnSorts[originalId]
  const active = sort.name === sortItem.name
  const order = active ? sort.order : sortItem.defaultOrder
  const handleSort = () => {
    if (isLoading || disabled || editable) return
    batch(() => {
      dispatch(
        setSort({
          name: sortItem.name,
          order: active ? oppositeSortOrder(sort.order) : sortItem.defaultOrder,
        })
      )
      dispatch(setPage(0))
    })
  }
  return (
    <BaseCell
      className={classNames('sortable', {
        sort_on: active,
      })}
      onClick={handleSort}>
      {title}
      {tooltip && (
        <HelpTooltip container="dashboardWrap" nowrap={false}>
          {tooltip}
        </HelpTooltip>
      )}
      {!disabled && !editable && (
        <NoneAnchor className="desc_sort">
          <SortArrow order={order} />
        </NoneAnchor>
      )}
    </BaseCell>
  )
}
SortableHeader.propTypes = {
  column: PropTypes.shape({
    originalId: PropTypes.string,
    title: PropTypes.string,
    tooltip: PropTypes.node,
  }),
  disabled: PropTypes.bool,
}

export const TitleHeader = () => {
  const dispatch = useDispatch()
  const { type, name } = useDashboardTypeName()
  const { sort, isLoading, editable } = useDashboardTableSelector()
  const { setSort, setPage } = useDashboardTableActions()
  const defaultSortItem =
    type === DashboardTypes.KEYWORD ? ColumnSorts.text : ColumnSorts.name
  const sortItems =
    type === DashboardTypes.CREATIVE
      ? [defaultSortItem, ColumnSorts.createdDate]
      : [defaultSortItem, ColumnSorts.createdDate, ColumnSorts.updatedDate]
  const activeSortItem = sortItems.find(s => s.name === sort.name)
  const order = activeSortItem ? sort.order : defaultSortItem.defaultOrder
  const handleTitleSort = () => {
    if (isLoading || editable) return
    batch(() => {
      dispatch(
        setSort({
          name: activeSortItem ? activeSortItem.name : defaultSortItem.name,
          order: activeSortItem
            ? oppositeSortOrder(sort.order)
            : defaultSortItem.defaultOrder,
        })
      )
      dispatch(setPage(0))
    })
  }
  const handleMenuSort = sortItem => {
    if (isLoading) return
    const active = activeSortItem && activeSortItem.name === sortItem.name
    batch(() => {
      dispatch(
        setSort({
          name: sortItem.name,
          order: active ? oppositeSortOrder(sort.order) : sortItem.defaultOrder,
        })
      )
      dispatch(setPage(0))
    })
  }
  return (
    <>
      <BaseCell
        className={classNames('sortable', {
          sort_on: activeSortItem,
        })}
        onClick={handleTitleSort}>
        {name}
        {!editable && (
          <>
            <Tooltip
              tag="a"
              wrapperClassName="btn_menu"
              position="bottom alignRight"
              display="block"
              containerId="dashboardWrap"
              className="menu_layer"
              content={
                <>
                  {sortItems.map(item => {
                    const active =
                      activeSortItem && activeSortItem.name === item.name
                    return (
                      <NoneAnchor
                        key={item.name}
                        className={classNames('link_menu', { on: active })}
                        onClick={event => {
                          event.stopPropagation()
                          handleMenuSort(item)
                        }}>
                        {item.title}
                        <SortArrow
                          order={active ? sort.order : item.defaultOrder}
                        />
                      </NoneAnchor>
                    )
                  })}
                </>
              }>
              <span className="ico_comm ico_menu">펼침/닫힘</span>
            </Tooltip>
            <NoneAnchor className="desc_sort">
              {activeSortItem ? activeSortItem.title : defaultSortItem.title}
              <SortArrow order={order} />
            </NoneAnchor>
          </>
        )}
      </BaseCell>
    </>
  )
}
TitleHeader.propTypes = {
  sort: PropTypes.shape({
    name: PropTypes.string,
    order: PropTypes.string,
  }),
}

export const MetricHeader = ({ column: { originalId } }) => {
  const { filterEnabled } = useSelector(state => state.dashboard.common)
  const { displayName: title, tooltip } = MetricColumns[originalId]
  const column = {
    originalId,
    title,
    tooltip,
  }
  return <SortableHeader column={column} disabled={!filterEnabled} />
}
MetricHeader.propTypes = {
  column: PropTypes.shape({
    originalId: PropTypes.string,
  }),
}

const InfoTotalTypes = {
  [DashboardTypes.CAMPAIGN]: DashboardInfoTypes.TOTAL_CAMPAIGN,
  [DashboardTypes.AD_GROUP]: DashboardInfoTypes.TOTAL_AD_GROUP,
  [DashboardTypes.KEYWORD]: DashboardInfoTypes.TOTAL_KEYWORD,
  [DashboardTypes.CREATIVE]: DashboardInfoTypes.TOTAL_CREATIVE,
}

export const SummaryTitleHeader = () => {
  const dispatch = useDispatch()
  const { totalElements, editable } = useDashboardTableSelector()
  const { type, name } = useDashboardTypeName()
  const { visible, type: infoType } = useSelector(state => state.dashboard.info)
  const showChartIcon = visible && !editable
  const totalType = InfoTotalTypes[type]
  return (
    <BaseCell>
      <span className="thumb_link">
        전체 {name} {totalElements}개
        <HelpTooltip>
          {type === DashboardTypes.KEYWORD && (
            <>
              프리미엄링크와 톡채널검색 데이터이며, 프리미엄링크의 콘텐츠 매체
              데이터는 포함하지 않습니다.
              <br />
            </>
          )}
          필터에 운영상태 미설정시 삭제 값도 합산되어 나타납니다.
        </HelpTooltip>
        {showChartIcon && (
          <div className="group_btn">
            <span
              className={classNames('link_chart show', {
                on:
                  (infoType === DashboardInfoTypes.TOTAL_CAMPAIGN &&
                    type === DashboardTypes.CAMPAIGN) ||
                  (infoType === DashboardInfoTypes.TOTAL_AD_GROUP &&
                    type === DashboardTypes.AD_GROUP) ||
                  (infoType === DashboardInfoTypes.TOTAL_KEYWORD &&
                    type === DashboardTypes.KEYWORD) ||
                  (infoType === DashboardInfoTypes.TOTAL_CREATIVE &&
                    type === DashboardTypes.CREATIVE),
              })}>
              <NoneAnchor
                className="link_g"
                onClick={() => {
                  dispatch(setAdgroupId(null))
                  dispatch(resetActives())
                  dispatch(setInfo({ type: totalType }))
                }}>
                <span className="ico_comm ico_chart">{name} 설정</span>
              </NoneAnchor>
            </span>
          </div>
        )}
      </span>
    </BaseCell>
  )
}

export const ContentMatchingHeader = () => {
  const dispatch = useDispatch()
  const { editable } = useDashboardTableSelector()
  const { visible, type } = useSelector(state => state.dashboard.info)
  const showChartIcon = visible && !editable
  return (
    <BaseCell>
      <span className="thumb_link">
        콘텐츠 매체
        <HelpTooltip>
          프리미엄링크 키워드가 속한 광고 그룹의 콘텐츠 매체 데이터입니다.
        </HelpTooltip>
        {showChartIcon && (
          <div className="group_btn">
            <span
              className={classNames('link_chart show', {
                on: type === DashboardInfoTypes.TOTAL_CONTENT_MATCHING,
              })}>
              <NoneAnchor
                className="link_g"
                onClick={() => {
                  dispatch(setAdgroupId(null))
                  dispatch(
                    setInfo({ type: DashboardInfoTypes.TOTAL_CONTENT_MATCHING })
                  )
                }}>
                <span className="ico_comm ico_chart">콘텐츠 매체 설정</span>
              </NoneAnchor>
            </span>
          </div>
        )}
      </span>
    </BaseCell>
  )
}

export const SummaryMetricHeader = ({ column: { id, originalId } }) => {
  const key = originalId || id
  const { summary, summaryLoading } = useDashboardTableSelector()
  const value = summary[key]
  const rateType = RateTypes.includes(key)
  return (
    <BaseCell>
      {summaryLoading ? (
        <Loading size="small" />
      ) : (
        <span className="align_r fw_bold">
          <NullableNumber
            value={value}
            thousandSeparator
            alternative={rateType ? '0%' : 0}
            suffix={rateType ? '%' : undefined}
          />
        </span>
      )}
    </BaseCell>
  )
}
SummaryMetricHeader.propTypes = {
  column: PropTypes.shape({
    id: PropTypes.string,
    originalId: PropTypes.string,
  }),
}

export const ExtraSummaryMetricHeader = ({ column: { id } }) => {
  const { extraSummary, summaryLoading } = useDashboardTableSelector()
  const value = extraSummary[id]
  const rateType = RateTypes.includes(id)
  return (
    <BaseCell>
      {summaryLoading ? (
        <Loading size="small" />
      ) : (
        <span className="align_r fw_bold">
          <NullableNumber
            value={value}
            thousandSeparator
            alternative={rateType ? '0%' : 0}
            suffix={rateType ? '%' : undefined}
          />
        </span>
      )}
    </BaseCell>
  )
}
ExtraSummaryMetricHeader.propTypes = {
  column: PropTypes.shape({
    id: PropTypes.string,
  }),
}
